import React, { useEffect, useState } from 'react';
import './style.scss';
import am4GeodataUsaLow from '@amcharts/amcharts4-geodata/usaLow';
import * as am4core from '@amcharts/amcharts4/core';
import * as am4maps from '@amcharts/amcharts4/maps';
import { AM4CHARTS_ABBREV_CAPITALIZED, ABBREV_TO_STATE_TITLECASE, getStateMap } from '../../constants';

const Turnout = (props) => {
  const {
    turnout,
    match: {
      params: {
        stateId,
      } = {},
    } = {},
  } = props;

  const [renderedMap, setRenderedMap] = useState();
  const [clickedState, setClickedState] = useState();
  const [yesCountyData, setYesCountyData] = useState();

  // Thomas helped pair code with me for the below
  // the useEffect hook below is for county geodata / voting data display
  useEffect(() => {
    // change map data if state is clicked on
    if (clickedState) {
      // if there is county level voting data for the state (ie if not Alaska)
      if (clickedState.county_data) {
      // create series for county data, if statement below allows deeplinking given different data object
        const countySeries = renderedMap.series.push(new am4maps.MapPolygonSeries());
        if (clickedState.name !== undefined) {
          const CountyMapData1 = getStateMap(clickedState.name);
          countySeries.geodata = CountyMapData1;
          // otherwise, deeplinked so get ID of deeplink state, convert to title case, and get county level data
        } else if (clickedState.id) {
          const stateName = ABBREV_TO_STATE_TITLECASE[clickedState.id.substr(3, 2)];
          const CountyMapData2 = getStateMap(stateName);
          countySeries.geodata = CountyMapData2;
        }

        // below is MIT Election Lab voting data, fips is unique county code
        const countyElectionData = clickedState.county_data.map(obj => ({
          ...obj,
          id: obj.fips,
          voter_turnout: Math.floor(((obj.clinton16 + obj.trump16 + obj.otherpres16) / obj.total_population) * 100),
        }));

        countySeries.data = countyElectionData;
        const countyPolygonTemplate = countySeries.mapPolygons.template;
        countyPolygonTemplate.tooltipText = 'County: {name} \n % Pop Voted in 2016: {voter_turnout}% \n Total Population: {total_population}';
        countyPolygonTemplate.fill = renderedMap.colors.getIndex(1);
        countyPolygonTemplate.nonScalingStroke = true;
      } else {
        // if this state has no county level data, add line of text to right hand side data to say there is no data
        setYesCountyData(false);
      }
    } else if (renderedMap) {
      // remove county data when zoom out
      while (renderedMap.series.length > 1) {
        renderedMap.series.pop();
      }
    }
  }, [clickedState]);

  // The use effect hook below allows deeplinking, this will update any time the map or stateID changes
  // I pair coded with Thomas on this deep linking feature. He was a great help.
  useEffect(() => {
    setYesCountyData(true);
    if (stateId) {
      const adjStateData = turnout.map(data => ({
        ...data,
        id: AM4CHARTS_ABBREV_CAPITALIZED[data.state],
        value: data.percent_voted_of_total_population,
      }));

      setClickedState(adjStateData.find(obj => obj.id === `US-${stateId}`));

      // the const code below essentially unpacks the map object and finds the relevant hitPolygon (ie the state in question)
      const {
        series: {
          values = [],
        } = {},
      } = renderedMap || {};

      // more unpacking
      const polygonSeries = values[0] || {};
      const polygons = polygonSeries?.mapPolygons?.values || [];

      if (polygons) {
        // more unpacking
        const hitPolygon = polygons.find((polygon) => {
          const {
            dataItem: {
              dataContext: {
                id,
              } = {},
            } = {},
          } = polygon;

          // note the format of state IDs in constants is US-${state abbreviation}
          return id === `US-${stateId}`;
        });

        // more unpacking
        const {
          chart = {},
        } = polygonSeries;

        // the if statement prevents crashing before chart is loaded
        if (chart) {
          if (chart.zoomToMapObject) {
            chart.zoomToMapObject(hitPolygon);
          }
        }
      }
    }
  }, [stateId, renderedMap]);

  useEffect(() => {
    // the map creation below using amcharts was adopted from here:
    // https://github.com/tmonfre/cs61-covid19-frontend
    const stateData = turnout.map(data => ({
      ...data,
      id: AM4CHARTS_ABBREV_CAPITALIZED[data.state],
      value: data.percent_voted_of_total_population,
    }));

    // create map of US states and territories
    const map = am4core.create('LHS', am4maps.MapChart);
    map.geodata = am4GeodataUsaLow;
    map.projection = new am4maps.projections.AlbersUsa();
    map.chartContainer.wheelable = false;

    // define state shape and data
    const polygonSeries = map.series.push(new am4maps.MapPolygonSeries());
    polygonSeries.useGeodata = true;
    polygonSeries.data = stateData;

    // set heat map fill rules
    polygonSeries.heatRules.push({
      property: 'fill',
      target: polygonSeries.mapPolygons.template,
      min: map.colors.getIndex(1).brighten(1),
      max: map.colors.getIndex(1).brighten(-0.3),
    });

    // set up heat legend
    const heatLegend = map.createChild(am4maps.HeatLegend);
    heatLegend.series = polygonSeries;
    heatLegend.align = 'left';
    heatLegend.valign = 'top';
    heatLegend.width = am4core.percent(40);
    heatLegend.marginRight = am4core.percent(6);
    heatLegend.marginLeft = am4core.percent(10);
    heatLegend.minValue = 30;
    heatLegend.maxValue = 75;

    // show where the figure stands on the heat map legend
    polygonSeries.mapPolygons.template.events.on('over', (ev) => {
      if (ev.target.dataItem.value) {
        heatLegend.valueAxis.showTooltipAt(ev.target.dataItem.value);
      } else {
        heatLegend.valueAxis.hideTooltip();
      }
    });

    polygonSeries.mapPolygons.template.events.on('out', (ev) => {
      heatLegend.valueAxis.hideTooltip();
    });

    // configure series tooltip
    const polygonTemplate = polygonSeries.mapPolygons.template;
    polygonTemplate.tooltipText = '{state}\n Percent Voted of Population: {percent_voted_of_total_population} \n Percent Registered of Population: {percent_registered_of_total_population}';
    polygonTemplate.nonScalingStroke = true;
    polygonTemplate.strokeWidth = 0.5;

    // Create hover state and set alternative fill color
    const hs = polygonTemplate.states.create('hover');
    hs.properties.fill = am4core.color('#4B5A82');
    map.tooltip.getFillFromObject = false;
    map.tooltip.background.fill = am4core.color('#c2c2c2');

    // allow state data to be extracted upon click
    // source: I used am4 charts docs to learn this
    // link: https://www.amcharts.com/docs/v4/tutorials/retrieving-information-of-a-clicked-map-object/
    polygonTemplate.events.on('hit', (ev) => {
      ev.target.series.chart.zoomToMapObject(ev.target);
      setClickedState(stateData.find(obj => obj.name === ev.target.dataItem.dataContext.name));
    });

    // adding a button to "go home" and zoom out post-click
    // source: I read the am4 charts docs for this
    // link: https://www.amcharts.com/docs/v4/tutorials/resetting-map-position-zoom/
    const home = map.chartContainer.createChild(am4core.Button);
    home.marginRight = 20;
    home.marginTop = 20;
    home.label.text = 'Reset Map';
    home.align = 'right';
    home.events.on('hit', (ev) => {
      map.goHome();
      setYesCountyData(true);
      setClickedState();
    });
    setRenderedMap(map);
  }, [turnout]);

  function NoStateClicked() {
    return (
      <div>
        Click on a state to see more detailed statistics
      </div>
    );
  }

  function NoCountyData() {
    return (
      <div className="noCountyData">
        This state does not report county-level data
      </div>
    );
  }

  function DetailedStateData() {
    return (
      <div>
        <div className="detailedCol">
          <div className="detailedTitle">
            {clickedState ? clickedState.state : 'State Data'}
          </div>
        </div>
        <div>
          <div className="detailedText">
            {yesCountyData
              ? <div />
              : <NoCountyData />}
          </div>
          <div className="detailedText">
            Total Citizen Population (in thousands): &nbsp;
            {clickedState.total_population}
          </div>
          <div className="detailedText">
            Total Population (in thousands): &nbsp;
            {clickedState.total_citizen_population}
          </div>
          <div className="detailedText">
            Percent Registered of Citizen Population: &nbsp;
            {clickedState.percent_registered_of_citizen_population}
          </div>
          <div className="detailedText">
            Percent Registered of Total Population: &nbsp;
            {clickedState.percent_registered_of_total_population}
          </div>
          <div className="detailedText">
            Total Registered Voters (in thousands): &nbsp;
            {clickedState.total_registered_voters}
          </div>
          <div className="detailedText">
            Percent Voted of Citizen Population: &nbsp;
            {clickedState.percent_voted_of_citizen_population}
          </div>
          <div className="detailedText">
            Percent Voted of Total Population: &nbsp;
            {clickedState.percent_voted_of_total_population}
          </div>
          <div className="bottomDetailedText">
            Total Voted (in thousands): &nbsp;
            {clickedState.total_voted}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="masterCol">
      <div className="pageTitle">
        <h1>Election Turnout Data</h1>
        <p>View information on turnout data from the 2020 General Election. Hover over states for detailed metrics.</p>
      </div>
      <div className="detailedReturn">
        <div id="LHS" />
        <div className="notClickedDisplay">
          {clickedState
            ? <DetailedStateData />
            : <NoStateClicked />}
        </div>
      </div>
    </div>
  );
};

export default Turnout;
